import styled from 'styled-components'
import WizrFlexLayout from '../WizrFlexLayout'
import WizrInput from '../WizrInput'
import { Form, notification } from 'antd'
import WizrText from '../WizrText'
import WizrFlexSpacing from '../WizrFlexSpacing'
import WizrButton from '../WizrButton'
import WizrDropDown from '../WizrDropDown'
import { useGetMasterIntegrationList } from '../../redux/selectors/content'
import { useEffect, useState, useRef } from 'react'
import { dispatchMasterIntegrationList } from '../../redux/actions/content'
import { fetchLLMModels } from '../../api/helpers/contents'
import ZendeskIcon from '../../assets/icons/ZendeskIcon'
import ServiceNow from '../../assets/icons/ServiceNowIcon'
import Freshdesk from '../../assets/icons/FreshdeskIcon'
import Salesforce from '../../assets/icons/Salesforce'
import ConfluenceIcon from '../../assets/icons/confluence'
import JiraIcon from '../../assets/icons/Jira'
import Slack from '../../assets/icons/Slack'
import SharepointIcon from '../../assets/icons/sharepoint'
import PostgreSQLIcon from '../../assets/icons/PostgreSQLIcon'
import WizrToggleButton from '../WizrToggleButton'
import WizrLoader from '../WizrLoader'
import Gemini from '../../icons/Gemini'
import Pinecone from '../../assets/icons/Pinecone'
import SendEmailIcon from '../../assets/icons/SendEmailIcon'
import MongoDBIcon from '../../assets/icons/MongoDbIcon'
import PgVectorIcon from '../../assets/icons/PgVectorIcon'
import MilvusIcon from '../../assets/icons/MilvusIcon'
import QdrantIcon from '../../assets/icons/QdrantIcon'
import WeaviateIcon from '../../assets/icons/WeaviateIcon'

const StyledContainer = styled(WizrFlexLayout)`
  gap: 8px;
  width: 100%;
  position: relative;
`
const StyledWizrInput = styled(WizrInput)`
  min-width: 25vw;
`
const InputContainer = styled(WizrFlexLayout)`
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  background-color: #fff;
  width: 100%;
`

const StyledDropDown = styled(WizrDropDown)`
  height: 52px;
  .ant-select-selector {
    height: 52px !important;
    background-color: #f2f4f9 !important;
    display: flex !important;
    align-items: center !important;
    .ant-select-selection-placeholder {
      display: flex;
      align-items: center;
    }
    .ant-select-selection-item {
      display: flex !important;
      align-items: center !important;
    }
  }
`

interface IIntegeationFormForm {
  integrationData?: { [key: string]: any }
  onFormSubmission: (params: { [key: string]: any }) => void
  onCancel: () => void
}

const AddIntegrations = ({
  integrationData,
  onFormSubmission,
  onCancel,
}: IIntegeationFormForm): JSX.Element => {
  const normalizessl = (data: any) => {
    if (!data) return data
    const ssl = data.ssl
    if (typeof ssl === 'string') return { ...data, ssl: ssl === 'require' }
    return data
  }

  const [currentIntegration, setCurrentIntegration] = useState(
    normalizessl(integrationData) ?? {
      display_name: '',
      integration_key: '',
      config_data: [],
      integration_display_name: '',
      registered_emailid: '',
      confluenceUrl: '',
      ssl: false
    },
  )

  const [currentConfig, setCurrentConfig] = useState(
    integrationData?.config_data ? integrationData?.config_data : [],
  )
  const [llmModels, setLlmModels] = useState<any[]>([])
  const [selectedLlmModel, setSelectedLlmModel] = useState<string>('')
  const [loadingLlmModels, setLoadingLlmModels] = useState<boolean>(false)
  const [form] = Form.useForm()

  // Validation state
  const [showIntegrationKeyValidation, setShowIntegrationKeyValidation] = useState(false)
  const [showDisplayNameValidation, setShowDisplayNameValidation] = useState(false)
  const [showEmailValidation, setShowEmailValidation] = useState(false)
  const [configFieldValidations, setConfigFieldValidations] = useState<{ [key: string]: boolean }>({})
  const [showLlmModelValidation, setShowLlmModelValidation] = useState(false)

  // Refs for error scrolling
  const integrationKeyRef = useRef<HTMLDivElement>(null)
  const displayNameRef = useRef<HTMLDivElement>(null)
  const emailRef = useRef<HTMLDivElement>(null)
  const configFieldRefs = useRef<{ [key: string]: React.RefObject<HTMLDivElement> }>({})
  const llmModelRef = useRef<HTMLDivElement>(null)

  // Validation function
  const validateAllFields = () => {
    const isBlank = (text?: string) => !text || text.trim() === ''
    const isValidEmail = (email?: string) => {
      if (!email) return false
      const emailRegex = /^[^\s@]+@[^\s@.]+\.[^\s@.]+$/
      return emailRegex.test(email)
    }

    let isValid = true
    let firstErrorRef: React.RefObject<HTMLDivElement> | null = null

    // Reset validation states
    setShowIntegrationKeyValidation(false)
    setShowDisplayNameValidation(false)
    setShowEmailValidation(false)
    setConfigFieldValidations({})
    setShowLlmModelValidation(false)

    // Validate integration_key (Select Tool)
    if (isBlank(currentIntegration?.integration_key)) {
      setShowIntegrationKeyValidation(true)
      if (!firstErrorRef) firstErrorRef = integrationKeyRef
      isValid = false
    }

    // Validate display_name (Name)
    if (isBlank(currentIntegration?.display_name)) {
      setShowDisplayNameValidation(true)
      if (!firstErrorRef) firstErrorRef = displayNameRef
      isValid = false
    }

    // Validate registered_emailid (Email ID) - only if shown
    const shouldShowEmail = currentIntegration?.integration_key !== 'slack' && 
                           currentIntegration?.integration_key !== 'servicenow' && 
                           currentIntegration?.integration_key !== 'postgres' && 
                           currentIntegration?.integration_key !== 'gemini' && 
                           currentIntegration?.integration_key !== 'pinecone' && 
                           currentIntegration?.integration_key !== 'smtp'
    
    if (shouldShowEmail) {
      if (isBlank(currentIntegration?.registered_emailid) || !isValidEmail(currentIntegration?.registered_emailid)) {
        setShowEmailValidation(true)
        if (!firstErrorRef) firstErrorRef = emailRef
        isValid = false
      }
    }

    // Validate config_data fields
    const newConfigValidations: { [key: string]: boolean } = {}
    currentConfig.forEach((item: any) => {
      // Skip Model Name field for Gemini (handled separately)
      if (currentIntegration?.integration_key === 'gemini' && isModelNameField(item)) {
        return
      }
      
      // Skip SSL field validation for SMTP (handled by toggle)
      if (currentIntegration?.integration_key === 'smtp' && 
          (item?.key_name?.toLowerCase().includes('ssl') || 
           item?.display_name?.toLowerCase().includes('ssl'))) {
        return
      }
      
      if (isBlank(item?.value)) {
        newConfigValidations[item?.key_name] = true
        if (!firstErrorRef) {
          if (!configFieldRefs.current[item?.key_name]) {
            configFieldRefs.current[item?.key_name] = { current: null }
          }
          firstErrorRef = configFieldRefs.current[item?.key_name]
        }
        isValid = false
      }
    })
    setConfigFieldValidations(newConfigValidations)

    // Validate LLM Model for Gemini integration
    if (currentIntegration?.integration_key === 'gemini' && isBlank(selectedLlmModel)) {
      setShowLlmModelValidation(true)
      if (!firstErrorRef) firstErrorRef = llmModelRef
      isValid = false
    }

    // Scroll to first error
    if (!isValid && firstErrorRef?.current) {
      setTimeout(() => {
        firstErrorRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 100)
    }

    return isValid
  }

  // Custom form submission handler to include LLM model in config_data
  const handleFormSubmission = (values: any) => {
    // Validate all fields before submission
    if (!validateAllFields()) {
      return
    }

    const formValues = { ...values }

    // Always include the current config_data with user-entered values
    let updatedConfig = [...currentConfig]

    // If LLM model is selected for Gemini integration, replace the Model Name field with our selected model
    if (selectedLlmModel && currentIntegration?.integration_key === 'gemini') {
      const modelNameIndex = updatedConfig.findIndex((config: any) =>
        config.key_name?.toLowerCase().includes('model') ||
        config.display_name?.toLowerCase().includes('model')
      )

      if (modelNameIndex !== -1) {
        updatedConfig[modelNameIndex] = {
          ...updatedConfig[modelNameIndex],
          value: selectedLlmModel
        }
      } else {
        updatedConfig.push({
          key_name: 'llm_model',
          display_name: 'LLM Model',
          value: selectedLlmModel
        })
      }
    }

    formValues.config_data = updatedConfig
    formValues.tool_type = currentIntegration?.tool_type

    // Only include SSL field for integrations that support it
    const sslIntegrations = ['postgres', 'smtp', 'pgvector', 'milvus', 'qdrant', 'weaviate']
    if (!sslIntegrations.includes(currentIntegration?.integration_key)) {
      delete formValues.ssl
    }

    onFormSubmission(formValues)
  }

  const masterIntegrations = useGetMasterIntegrationList()
  useEffect(() => {
    dispatchMasterIntegrationList()
  }, [])

  // For vector_db edits: once master list loads, rebuild config fields from the
  // master template so field labels/keys are always current, while preserving
  // any values the backend already resolved (via _format_vector_config).
  useEffect(() => {
    if (!integrationData?.vector_db || !masterIntegrations?.length) return
    const master = masterIntegrations.find(
      (m: any) => m.integration_key === integrationData.integration_key,
    )
    if (!master?.config_data?.length) return

    // Primary: values from the backend-resolved config_data (already has correct key_names)
    const resolvedByKey: { [key: string]: string } = {}
    ;(integrationData.config_data ?? []).forEach((item: any) => {
      if (item.key_name && item.value != null && item.value !== '') {
        resolvedByKey[item.key_name] = item.value
      }
    })

    // Fallback: read flat fields directly from the integration record using the
    // same prefix-stripping logic as _resolve_vector_field_value on the backend.
    // e.g. key_name "pinecone_index" → flat field integrationData["pinecone_index"]
    //      key_name "pgvector_host"  → bare key "host" → integrationData["host"]
    const prefix = `${integrationData.integration_key}_`
    const resolveValue = (key_name: string): string => {
      if (resolvedByKey[key_name] !== undefined) return resolvedByKey[key_name]
      // Try the flat field first (e.g. integrationData["pinecone_index"])
      if (integrationData[key_name] != null && integrationData[key_name] !== '') {
        return integrationData[key_name]
      }
      // Then try the bare key without the integration prefix (e.g. "host" for pgvector)
      const bareKey = key_name.startsWith(prefix) ? key_name.slice(prefix.length) : key_name
      return integrationData[bareKey] ?? ''
    }

    const mergedConfig = master.config_data.map((field: any) => ({
      key_name: field.key_name,
      display_name: field.display_name,
      payload_key: field.payload_key,
      value: resolveValue(field.key_name),
    }))

    setCurrentConfig(mergedConfig)
    mergedConfig.forEach((item: any) => {
      form.setFieldsValue({ [item.key_name]: item.value })
    })
  }, [masterIntegrations, integrationData?.id])

  // Initialize SSL toggle when integration key changes
  useEffect(() => {
    const sslIntegrations = ['postgres', 'smtp', 'pgvector', 'milvus', 'qdrant', 'weaviate']
    if (sslIntegrations.includes(currentIntegration?.integration_key)) {
      const sslBool = currentIntegration?.ssl === undefined ? false : Boolean(currentIntegration?.ssl)
      if (currentIntegration?.ssl === undefined) {
        setCurrentIntegration({ ...currentIntegration, ssl: sslBool })
      }
      form.setFieldsValue({ ssl: sslBool })
    }
  }, [currentIntegration?.integration_key])

  const getIcon = (item: any) => {
    switch (item?.integration_key) {
      case 'zendesk':
        return <ZendeskIcon />
      case 'freshdesk':
        return <Freshdesk/> 
        case 'salesforce':
        return <Salesforce/> 
        case 'confluence':
        return <ConfluenceIcon/> 
        case 'jira':
        return <JiraIcon/> 
        case 'slack':
        return <Slack/>
        case 'sharepoint':
          return <SharepointIcon/>
        case 'servicenow':
          return <ServiceNow/>
        case 'postgres':
          return <PostgreSQLIcon/>
        case 'gemini':
          return <Gemini/>
        case 'pinecone':
          return <Pinecone/>
        case 'mongodb':
          return <MongoDBIcon />
        case 'smtp':
          return <SendEmailIcon/>
        case 'pgvector':
          return <PgVectorIcon/>
        case 'milvus':
          return <MilvusIcon/>
        case 'qdrant':
          return <QdrantIcon/>
        case 'weaviate':
          return <WeaviateIcon/>
      default:
        return null
    }
  }

  const dropDownConverter = (value: string[]) => {

    const result =
      value?.length > 0 &&
      value?.map((item: any, key: number) => ({
        label: (
          <div style={{ alignItems: 'center', display: 'flex', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>{getIcon(item)}</div>
            <div style={{ display: 'flex', alignItems: 'center', lineHeight: 'normal' }}>{item.display_name || ''}</div>
          </div>
        ),
        value: item.integration_key || '',
      }))
    return result
  }


  const onToolSelection = (value: any, label: any) => {
    masterIntegrations.forEach((item: any) => {
      if (item?.integration_key === value) {
        setCurrentConfig(item?.config_data)
        const sslValue = false
        form.setFieldsValue({ 
          integration_key: value,
          integration_display_name: item?.display_name,
          ssl: sslValue
        })
        setCurrentIntegration({
          ...currentIntegration,
          integration_key: value,
          integration_display_name: item?.display_name,
          tool_type: item?.tool_type,
          ssl: sslValue // Set SSL to false for postgres and smtp by default
        })

        // Clear LLM models and selection when switching away from Gemini
        if (value !== 'gemini') {
          setLlmModels([])
          setSelectedLlmModel('')
        }

        // Clear validation errors when tool is selected
        setShowIntegrationKeyValidation(false)
      }
    })
  }

  useEffect(() => {
    form.setFieldsValue({ config_data: currentConfig })
  }, [currentConfig])

  const setCurrentConfigValue = (value: any, item: any) => {
    const index = currentConfig.findIndex((currentConfig: any) => currentConfig.key_name === item.key_name);

    // If the item is found in the array
    if (index !== -1) {
      // Create a copy of the currentConfig array
      const updatedConfig = [...currentConfig];

      // Update the value of the found item
      updatedConfig[index] = { ...item, value: value };

      // Update the state with the modified array
      setCurrentConfig(updatedConfig);
      form.setFieldsValue({ config_data: updatedConfig })

      // Clear validation error for this field when value is entered
      if (value && configFieldValidations[item.key_name]) {
        setConfigFieldValidations(prev => {
          const newValidations = { ...prev }
          delete newValidations[item.key_name]
          return newValidations
        })
      }
    }
  }

  // Function to check if a field is an API key field for Gemini
  const isApiKeyField = (item: any) => {
    return currentIntegration?.integration_key === 'gemini' &&
      (item?.key_name?.toLowerCase().includes('api') ||
        item?.key_name?.toLowerCase().includes('key') ||
        item?.display_name?.toLowerCase().includes('api') ||
        item?.display_name?.toLowerCase().includes('key'))
  }

  // Function to check if a field is the Model Name field for Gemini
  const isModelNameField = (item: any) => {
    return currentIntegration?.integration_key === 'gemini' &&
      (item?.key_name?.toLowerCase().includes('model') ||
        item?.display_name?.toLowerCase().includes('model'))
  }

  // Function to handle API key field events (Enter key or blur)
  const handleApiKeyFieldEvent = (value: any, item: any) => {
    if (isApiKeyField(item) && value?.trim()) {
      fetchLlmModelsForGemini(value.trim())
    }
  }

  // Function to fetch LLM models for Gemini integration
  const fetchLlmModelsForGemini = async (apiKey: string) => {
    if (!apiKey) return

    setLoadingLlmModels(true)
    try {
      const response = await fetchLLMModels(apiKey)
      if (response?.data?.status === 200 && response?.data?.data) {
        setLlmModels(response.data.data)
      } else {
        setLlmModels([])
      }
    } catch (error) {
      console.error('Error fetching LLM models:', error)
      setLlmModels([])
      notification.error({
        message: 'Failed to fetch models',
        description: 'No LLM models found or invalid response',
        duration: 2,
        placement: 'topRight',
      })
    } finally {
      setLoadingLlmModels(false)
    }
  }

  // Function to detect if a field is a password field
  const isPasswordField = (item: any) => {
    const keyName = item?.key_name?.toLowerCase() || '';
    const displayName = item?.display_name?.toLowerCase() || '';

    // Check for common password field patterns
    return keyName.includes('password') ||
      keyName.includes('pass') ||
      keyName.includes('pwd') ||
      displayName.includes('password') ||
      displayName.includes('pass') ||
      displayName.includes('pwd') ||
      keyName.includes('secret') ||
      displayName.includes('secret') ||
      keyName.includes('token') ||
      displayName.includes('token') ||
      keyName.includes('key') ||
      displayName.includes('key');
  }

  useEffect(() => {
    if (integrationData) {
      form.setFieldsValue({
        integration_key: integrationData?.integration_key,
        display_name: integrationData?.display_name,
        // Don't set config_data as a form field - it's handled separately
        integration_display_name: integrationData?.integration_display_name,
        registered_emailid: integrationData?.registered_emailid,
        confluenceUrl: integrationData?.confluenceUrl,
        ssl: typeof integrationData?.ssl === 'string' ? integrationData.ssl === 'require' : (integrationData?.ssl ?? false)
      })

      // If editing a Gemini integration, check for existing LLM model selection
      if (integrationData?.integration_key === 'gemini') {
        // Look for model_name field in config_data (this is how it's stored in the API response)
        const modelNameConfig = integrationData?.config_data?.find((config: any) =>
          config.key_name?.toLowerCase().includes('model') ||
          config.display_name?.toLowerCase().includes('model')
        )
        if (modelNameConfig?.value) {
          setSelectedLlmModel(modelNameConfig.value)
        }

        // Also fetch available LLM models for the dropdown
        const apiKeyConfig = integrationData?.config_data?.find((config: any) =>
          config.key_name?.toLowerCase().includes('api') ||
          config.key_name?.toLowerCase().includes('key')
        )
        if (apiKeyConfig?.value) {
          fetchLlmModelsForGemini(apiKeyConfig.value)
        }
      }
    }
    // For vector_db integrations the config fields are rebuilt from the master
    // template in a separate effect — skip here to avoid overwriting with stale values.
    if (!integrationData?.vector_db) {
      integrationData?.config_data?.map((item: any) => {
        // Don't set llm_model as a form field - it's handled separately
        if (item.key_name !== 'llm_model') {
          form.setFieldsValue({ [item.key_name]: item.value })
        }
      })
    }
  }, [integrationData])
  return (
    <StyledContainer background='#FFF' justifyContent='space-evenly'>
      <div style={{ width: '100%', position: 'relative' }}>
        <WizrFlexLayout
          flexDirection='column'
          justifyContent='flex-start'
          background='#fff'
          alignItems='flex-start'
          style={{ width: '100%', gap: '16px' }}
        >
        <Form
          name='basic'
          form={form}
          initialValues={{ remember: true }}
          onFinish={handleFormSubmission}
          onFinishFailed={() => null}
          autoComplete='off'
          style={{ width: '100%' }}
        >
          <Form.Item name='integration_key' >
            <InputContainer ref={integrationKeyRef}>
              <WizrText type='body2'>Select Tool</WizrText>
              <StyledDropDown
                value={currentIntegration?.integration_key}
                options={dropDownConverter(masterIntegrations)}
                onChange={(value: any,label:any) => {onToolSelection(value,label)}}
                showError={showIntegrationKeyValidation}
                errorMessage={showIntegrationKeyValidation ? "Please Select a Tool" : undefined}
              />
            </InputContainer>
          </Form.Item>
          <Form.Item name='display_name'>
            <InputContainer ref={displayNameRef}>
              <WizrText type='body2'>Name</WizrText>
              <WizrInput
                style={{ width: '100%' }}
                background={'#F2F4F9'}
                value={currentIntegration?.display_name}
                onChange={(value: any) => {
                  setCurrentIntegration({ ...currentIntegration, display_name: value })
                  if (value && showDisplayNameValidation) {
                    setShowDisplayNameValidation(false)
                  }
                }}
                placeholder="Enter name"
                showError={showDisplayNameValidation}
                errorMessage={showDisplayNameValidation ? "Please Enter a Name" : undefined}
              ></WizrInput>
            </InputContainer>
          </Form.Item>

        {currentIntegration?.integration_key !== 'slack' && currentIntegration?.integration_key !== 'servicenow' && currentIntegration?.integration_key !== 'postgres' && currentIntegration?.integration_key !== 'gemini' && currentIntegration?.integration_key !== 'pinecone' && currentIntegration?.integration_key !== 'smtp' && (
          <Form.Item
            name='registered_emailid'
          >
            <InputContainer ref={emailRef}>
              <WizrText type='body2'>Email ID</WizrText>
              <WizrInput
                style={{ width: '100%' }}
                background={'#F2F4F9'}
                value={currentIntegration?.registered_emailid}
                onChange={(value: any) => {
                  setCurrentIntegration({ ...currentIntegration, registered_emailid: value })
                  if (value && showEmailValidation) {
                    setShowEmailValidation(false)
                  }
                }}
                placeholder="Enter email address"
                showError={showEmailValidation}
                errorMessage={(() => {
                  if (!showEmailValidation) return undefined
                  return currentIntegration?.registered_emailid ? "Please Enter a Valid Email Address" : "Please Enter an Email Address"
                })()}
              ></WizrInput>
            </InputContainer>
          </Form.Item>
        )}

        <Form.Item name='config_data'>
          {currentConfig.map((item: any, index: any) => {
            // Skip rendering the Model Name field for Gemini integration - we'll show our LLM Models dropdown instead
            if (currentIntegration?.integration_key === 'gemini' && isModelNameField(item)) {
              return null
            }

            // Skip rendering SSL field for integrations that use the toggle instead
            const sslToggleIntegrations = ['smtp', 'postgres', 'pgvector', 'milvus', 'qdrant', 'weaviate']
            if (sslToggleIntegrations.includes(currentIntegration?.integration_key) &&
                (item?.key_name?.toLowerCase().includes('ssl') ||
                 item?.display_name?.toLowerCase().includes('ssl'))) {
              return null
            }

            // Initialize ref for this config field if not exists
            if (!configFieldRefs.current[item?.key_name]) {
              configFieldRefs.current[item?.key_name] = { current: null }
            }

            return (
              <div key={item?.key_name}>
                <Form.Item name={item?.key_name}>
                  <InputContainer ref={configFieldRefs.current[item?.key_name]}>
                    <WizrText type='body2'>{item?.display_name}</WizrText>
                    <WizrInput
                      style={{ width: '100%' }}
                      background={'#F2F4F9'}
                      value={item?.value}
                      onChange={(value: any) => setCurrentConfigValue(value, item)}
                      onPressEnter={(e: any) => handleApiKeyFieldEvent(e.target.value, item)}
                      onBlur={(e: any) => handleApiKeyFieldEvent(e.target.value, item)}
                      type={isPasswordField(item) ? 'password' : 'text'}
                      placeholder={`Enter ${item?.display_name}`}
                      showError={configFieldValidations[item?.key_name]}
                      errorMessage={configFieldValidations[item?.key_name] ? `Please Enter ${item?.display_name}` : undefined}
                    ></WizrInput>
                  </InputContainer>
                </Form.Item>

                {/* Show LLM Models dropdown and loader right after API key field */}
                {isApiKeyField(item) && currentIntegration?.integration_key === 'gemini' && (
                  <>
                    {/* Loading indicator for LLM Models API call */}
                    {loadingLlmModels && (
                      <InputContainer>
                        <WizrLoader 
                          loading={true} 
                          text="Fetching LLM Models..." 
                          size="small"
                        />
                      </InputContainer>
                    )}

                    {/* LLM Models Dropdown - Only show when models are loaded */}
                    {llmModels.length > 0 && (
                      <Form.Item name='llm_model'>
                      <InputContainer ref={llmModelRef}>
                        <WizrText type='body2'>LLM Models</WizrText>
                        <StyledDropDown
                          placeHolder={loadingLlmModels ? 'Loading models...' : 'Select LLM Model'}
                          disabled={loadingLlmModels}
                          value={selectedLlmModel}
                          showSearch={true}
                          options={llmModels.map((model: any) => ({
                            label: model.name || model.model_name || model.display_name || model,
                            value: model.id || model.model_id || model.name || model.model_name || model
                          }))}
                          onChange={(value: string) => {
                            setSelectedLlmModel(value)
                            if (value && showLlmModelValidation) {
                              setShowLlmModelValidation(false)
                            }
                          }}
                          showError={showLlmModelValidation}
                          errorMessage={showLlmModelValidation ? "Please Select an LLM Model" : undefined}
                        />
                      </InputContainer>
                      </Form.Item>
                    )}
                  </>
                )}
              </div>
            )
          })}
        </Form.Item>


        {/* SSL Toggle - Show for PostgreSQL, SMTP, and all vector DB integrations */}
        {(['postgres', 'smtp', 'pgvector', 'milvus', 'qdrant', 'weaviate'].includes(currentIntegration?.integration_key)) && (
          <Form.Item name='ssl'>
            <InputContainer>
              <WizrFlexLayout 
                flexDirection='row' 
                justifyContent='space-between' 
                alignItems='center'
                background='#fff'
                style={{ width: '100%' }}
              >
                <WizrText type='body2'>Enable SSL</WizrText>
                <WizrToggleButton
                  isEnabled={currentIntegration?.ssl}
                  onChange={(checked: boolean) => {
                    setCurrentIntegration({ ...currentIntegration, ssl: checked })
                    form.setFieldsValue({ ssl: checked })
                  }}
                />
              </WizrFlexLayout>
            </InputContainer>
          </Form.Item>
        )}

        <Form.Item>
          <WizrFlexSpacing
            justifyContent='flex-end'
            flexDirection='row'
            size='large'
            background='#FFF'
          >
            <Form.Item name='integration_display_name' >

            </Form.Item>
            <WizrButton type='default' text='Cancel' onClick={onCancel} style={{ borderRadius: '20px' }} />
            <WizrButton htmlType='submit' text='Save' style={{ borderRadius: '20px' }} />
          </WizrFlexSpacing>
        </Form.Item>
      </Form>
      </WizrFlexLayout>
      </div>
    </StyledContainer>
  )
}

            

export default AddIntegrations
